import { ArrowRight, Mail, Mountain, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';

import { authClient } from '../lib/auth';
import { request, workspace } from '../lib/engine';
import { production } from '../lib/runtime';
import { Button, Disclosure, Input } from './ui';

type Mode = 'signin' | 'signup' | 'setup' | 'totp' | 'recover' | 'reset' | 'mail';
type MailMessage = { to: string; subject: string; url: string; createdAt: string };

export function AuthGate({ notice }: { notice: string }) {
  const resetToken = new URLSearchParams(location.search).get('token');
  const [mode, setMode] = useState<Mode>(resetToken ? 'reset' : 'signin');

  const [email, setEmail] = useState(''),
    [password, setPassword] = useState(''),
    [name, setName] = useState('');

  const [code, setCode] = useState(''),
    [uri, setUri] = useState(''),
    [qr, setQr] = useState('');

  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [info, setInfo] = useState('');

  const [messages, setMessages] = useState<MailMessage[]>([]);

  useEffect(() => {
    if (uri) {
      QRCode.toDataURL(uri, { margin: 2, width: 180 })
        .then(setQr)
        .catch(() => {});
    }
  }, [uri]);

  const check = (result: { error?: { message?: string } | null }) => {
    if (result.error) {
      throw new Error(result.error.message ?? 'Please try again.');
    }
  };

  async function enable() {
    const result = await authClient.twoFactor.enable({ password });

    check(result);

    if (result.data?.method !== 'totp') {
      throw new Error('Authenticator setup could not start.');
    }

    setUri(result.data.totpURI);
    setMode('setup');
    setCode('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setInfo('');

    try {
      if (!navigator.onLine) {
        throw new Error('An online connection is required to sign in.');
      }

      await workspace.finishPendingLogout();

      if (mode === 'signup') {
        const result = await authClient.signUp.email({ email, password, name: name || 'Personal' });

        check(result);
        await enable();
      } else if (mode === 'signin') {
        const result = await authClient.signIn.email({ email, password });

        check(result);

        if ((result.data as { twoFactorRedirect?: boolean })?.twoFactorRedirect) {
          setMode('totp');
          setCode('');
        } else {
          await enable();
        }
      } else if (mode === 'setup' || mode === 'totp') {
        const result = await authClient.twoFactor.verifyTotp({ code, trustDevice: false });

        check(result);
        setPassword('');
        setUri('');
        setCode('');
        await workspace.open();
      } else if (mode === 'recover') {
        const result = await authClient.requestPasswordReset({
          email,
          redirectTo: `${location.origin}/?recovery=1`,
        });

        check(result);

        setInfo(
          production
            ? 'If this account exists, a recovery message has been sent to its email address.'
            : 'If this test account exists, its recovery message is in the local test mailbox.',
        );
      } else if (mode === 'reset') {
        const result = await authClient.resetPassword({
          token: resetToken ?? '',
          newPassword: password,
        });

        check(result);
        history.replaceState({}, '', '/');
        setPassword('');
        setMode('signin');

        setInfo(
          'Password reset. Sign in to enroll your authenticator again and recover your encrypted workspace.',
        );
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const titles: Record<Mode, string> = {
    signin: 'Welcome to your workbench.',
    signup: 'Make a little room.',
    setup: 'Add your authenticator.',
    totp: 'One more step.',
    recover: 'Recover your workspace.',
    reset: 'Choose a new password.',
    mail: 'Local test mailbox',
  };

  return (
    <div className="auth-page">
      <div className="auth-intro">
        <div className="brand">
          <Mountain size={29} />
          Cairn
        </div>
        <div>
          <span className="eyebrow">YOUR PERSONAL WORKBENCH</span>
          <h1>
            A place for your thoughts.
            <br />A path for your day.
          </h1>
          <p>
            Keep the next step close. Bring your notes, priorities, and working context together.
          </p>
          <div className="auth-preview">
            <span className="preview-dot" />
            Pick up where you left off.<div>One concrete action. A little visible progress.</div>
          </div>
        </div>
        <span className="auth-footnote">
          Workbench · {production ? 'Personal workspace' : 'Local preview'}
        </span>
      </div>
      <main className="auth-form-panel">
        <div className="auth-form-inner">
          <span className="eyebrow">{mode === 'mail' ? 'NO EMAIL IS SENT' : 'CAIRN'}</span>
          <h2>{titles[mode]}</h2>
          {notice && <p className="notice">{notice}</p>}
          {mode === 'mail' ? (
            <>
              <p className="muted">
                This isolated mailbox is for local test accounts only. These links can recover a
                test account without its authenticator.
              </p>
              {messages.length ? (
                messages.map(message => (
                  <a className="mail-item" key={message.url} href={message.url}>
                    <Mail size={16} />
                    <div>
                      <strong>{message.subject}</strong>
                      <span>
                        {message.to} · {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <ArrowRight size={15} />
                  </a>
                ))
              ) : (
                <p className="empty-message">No recovery messages yet.</p>
              )}
              <Button variant="ghost" onClick={() => setMode('signin')}>
                Back to sign in
              </Button>
            </>
          ) : (
            <form onSubmit={submit}>
              {mode === 'signup' && (
                <label>
                  Your name
                  <Input
                    autoComplete="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="What should we call you?"
                  />
                </label>
              )}
              {['signin', 'signup', 'recover'].includes(mode) && (
                <label>
                  {production ? 'Email' : 'Test email'}
                  <Input
                    type="email"
                    autoComplete="username"
                    placeholder={production ? 'Your email address' : 'you@cairn.test'}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </label>
              )}
              {['signin', 'signup', 'reset'].includes(mode) && (
                <label>
                  Password
                  <Input
                    type="password"
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    placeholder="At least 12 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    minLength={12}
                    required
                  />
                </label>
              )}
              {mode === 'setup' && (
                <div className="authenticator-setup">
                  <p>Scan this code with your authenticator, then enter its six-digit code.</p>
                  {qr && <img className="qr" src={qr} alt="Authenticator setup QR code" />}
                  <Disclosure summary="Enter a setup key manually">
                    <code className="setup-key">
                      {uri ? new URL(uri).searchParams.get('secret') : ''}
                    </code>
                  </Disclosure>
                </div>
              )}
              {['setup', 'totp'].includes(mode) && (
                <label>
                  Authenticator code
                  <Input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    className="totp-input"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                  />
                </label>
              )}
              {error && (
                <p role="alert" className="error-message">
                  {error}
                </p>
              )}
              {info && (
                <p role="status" className="notice">
                  {info}
                </p>
              )}
              <Button variant="primary" className=" auth-submit" disabled={busy} type="submit">
                {busy
                  ? 'One moment…'
                  : {
                      signin: 'Sign in',
                      signup: 'Create test workspace',
                      setup: 'Verify & open Cairn',
                      totp: 'Verify & open Cairn',
                      recover: 'Send recovery message',
                      reset: 'Reset password',
                      mail: '',
                    }[mode]}
              </Button>
            </form>
          )}
          {mode === 'signin' && (
            <div className="auth-links">
              {!production && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMode('signup');
                    setError('');
                  }}
                >
                  Create a test workspace
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  setMode('recover');
                  setError('');
                }}
              >
                Forgot password?
              </Button>
            </div>
          )}
          {mode === 'totp' && (
            <Button
              variant="ghost"
              className=" back-link"
              onClick={() => {
                setMode('recover');
                setError('');
              }}
            >
              Lost your authenticator? Recover by email
            </Button>
          )}
          {mode === 'reset' && (
            <Button
              variant="ghost"
              className=" back-link"
              onClick={() => {
                history.replaceState({}, '', '/');
                setPassword('');
                setMode('signin');
                setError('');
              }}
            >
              Back to sign in
            </Button>
          )}
          {['signup', 'recover'].includes(mode) && (
            <Button
              variant="ghost"
              className=" back-link"
              onClick={() => {
                setMode('signin');
                setError('');
              }}
            >
              Back to sign in
            </Button>
          )}
          {!production && (
            <div className="prototype-note">
              <ShieldCheck size={17} />
              <p>
                Use a <strong>.test</strong> email and test data. This prototype runs on your
                computer; the vault is not ready for real secrets.
              </p>
            </div>
          )}
          {!production && mode !== 'mail' && (
            <Button
              variant="ghost"
              className=" mailbox-link"
              onClick={() => {
                request<MailMessage[]>('/api/lab/mail')
                  .then(data => {
                    setMessages(data);
                    setMode('mail');
                  })
                  .catch(e => setError(e.message));
              }}
            >
              Open local test mailbox
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
